import { FC, ReactNode } from 'react';
import { Box, FormLabel, Typography } from '@mui/material';
import {
  ON_LIGHT_LABEL_SX,
  ON_PRIMARY_ERROR_SX,
  ON_PRIMARY_LABEL_SX,
} from '../fieldStyles';

// Label + field of the XD modal forms ("Patterns to respect" in DESIGN.md):
// the label to the left of the field, right-aligned, over the blue
// background. On mobile it goes above the field: on the left it would leave
// the field too narrow.
//
// `htmlFor` links the label to the field when the latter is a single input;
// the groups (switches, chips) use `component="p"` with `aria-labelledby` on
// the group. Extracted from pages/CreateUser/UserFormFields so that the
// archiving wizard uses the same row.
//
// `tone="light"` is the same row over a light background (the white card of
// public verification): only the label colour changes.

export const FORM_ROW_LABEL_WIDTH = 150;

type Props = {
  label: string;
  htmlFor?: string;
  id?: string;
  error?: string;
  /** Aligns the label with the first line of a tall group. */
  alignTop?: boolean;
  labelWidth?: number;
  /** Background the row sits on: the modal's blue (default) or light. */
  tone?: 'primary' | 'light';
  children: ReactNode;
};

const FormRow: FC<Props> = ({
  label,
  htmlFor,
  id,
  error,
  alignTop = false,
  labelWidth = FORM_ROW_LABEL_WIDTH,
  tone = 'primary',
  children,
}) => (
  <Box
    sx={(theme) => ({
      display: 'grid',
      gridTemplateColumns: `${labelWidth}px minmax(0, 1fr)`,
      columnGap: 2,
      alignItems: alignTop ? 'start' : 'center',
      [theme.breakpoints.down('tablet')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        rowGap: 0.75,
      },
    })}
  >
    <FormLabel
      htmlFor={htmlFor}
      id={id}
      component={htmlFor ? 'label' : 'p'}
      sx={(theme) => ({
        ...(tone === 'light' ? ON_LIGHT_LABEL_SX : ON_PRIMARY_LABEL_SX),
        textAlign: 'right',
        pt: alignTop ? 1 : 0,
        m: 0,
        [theme.breakpoints.down('tablet')]: { textAlign: 'left', pt: 0 },
      })}
    >
      {label}
    </FormLabel>
    <Box>
      {children}
      {error && (
        <Typography role="alert" sx={ON_PRIMARY_ERROR_SX}>
          {error}
        </Typography>
      )}
    </Box>
  </Box>
);

export default FormRow;
